import type { HealthTrendDataPoint } from "@/services/core-api-adapter";
import type { BloodPressureDataPoint } from "@/features/HealthScore/components/VitalsCategory/BloodPressure/BloodPressureChart";
import type { HeartRateDataPoint } from "@/features/HealthScore/components/VitalsCategory/HeartRate/HeartRateChart";
import type { BreathingRateDataPoint } from "@/features/HealthScore/components/VitalsCategory/BreathingRate/BreathingRateChart";
import type { BMIChartDataPoint } from "@/features/HealthScore/components/HealthHistoryCategory/BMI/BMIChart";
import type { SleepChartDataPoint } from "@/features/HealthScore/components/LifestyleCategory/Sleep/SleepChart";
import type { ExerciseChartDataPoint } from "@/features/HealthScore/components/LifestyleCategory/Exercise/ExerciseChart";
import { MentalWellbeingChartDataPoint } from "../components/MentalWellbeingCategory/MentalWellbeingChart/index";

/**
 * Format an ISO timestamp into chart display formats.
 * Returns date as "DD/MM" and fullDate as "DD.MM.YYYY".
 */
export function formatTimestampToChartDate(isoTimestamp: string): {
  date: string;
  fullDate: string;
} {
  try {
    const d = new Date(isoTimestamp);
    if (!Number.isFinite(d.getTime())) {
      return { date: "", fullDate: "" };
    }
    const day = String(d.getDate()).padStart(2, "0");
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const year = d.getFullYear();
    return {
      date: `${day}/${month}`,
      fullDate: `${day}.${month}.${year}`,
    };
  } catch {
    return { date: "", fullDate: "" };
  }
}

function parseValue(value: string): number {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

/**
 * Map API health trend points to BMI chart data.
 */
export function mapHealthTrendToBmiChartData(
  points: HealthTrendDataPoint[]
): BMIChartDataPoint[] {
  return points
    .map((p) => {
      const { date, fullDate } = formatTimestampToChartDate(p.timestamp);
      return {
        date,
        fullDate,
        bmi: parseValue(p.value),
      };
    })
    .reverse();
}

/**
 * Map API health trend points to heart rate chart data (Pulse = heart rate).
 */
export function mapHealthTrendToHeartRateChartData(
  points: HealthTrendDataPoint[]
): HeartRateDataPoint[] {
  return points
    .map((p) => {
      const { date, fullDate } = formatTimestampToChartDate(p.timestamp);
      return {
        date,
        fullDate,
        heartRate: parseValue(p.value),
      };
    })
    .reverse();
}

/**
 * Map API health trend points to breathing rate chart data.
 */
export function mapHealthTrendToBreathingRateChartData(
  points: HealthTrendDataPoint[]
): BreathingRateDataPoint[] {
  return points
    .map((p) => {
      const { date, fullDate } = formatTimestampToChartDate(p.timestamp);
      return {
        date,
        fullDate,
        breathingRate: parseValue(p.value),
      };
    })
    .reverse();
}

/** Normalize ISO timestamp to minute precision (YYYY-MM-DDTHH:mm) for pairing readings that differ by seconds. */
function normalizedMinuteKey(isoTimestamp: string): string {
  return isoTimestamp.slice(0, 16);
}

/**
 * Merge systolic and diastolic trend arrays into blood pressure chart data.
 * Reverses input arrays first (so API newest-first becomes oldest-first), then pairs by
 * timestamp normalized to minute (hour and minutes only; seconds ignored) so readings
 * a few seconds apart match. One point per minute when both sys and dia exist.
 * Result is ordered ascending (oldest first, latest last).
 */
export function mapHealthTrendsToBloodPressureChartData(
  systolicPoints: HealthTrendDataPoint[],
  diastolicPoints: HealthTrendDataPoint[]
): BloodPressureDataPoint[] {
  const systolicReversed = [...systolicPoints].reverse();
  const diastolicReversed = [...diastolicPoints].reverse();

  const systolicByMinute = new Map<
    string,
    { value: number; timestamp: string }
  >();
  for (const p of systolicReversed) {
    const key = normalizedMinuteKey(p.timestamp);
    const value = parseValue(p.value);
    const existing = systolicByMinute.get(key);
    if (!existing || p.timestamp > existing.timestamp) {
      systolicByMinute.set(key, { value, timestamp: p.timestamp });
    }
  }
  const diastolicByMinute = new Map<
    string,
    { value: number; timestamp: string }
  >();
  for (const p of diastolicReversed) {
    const key = normalizedMinuteKey(p.timestamp);
    const value = parseValue(p.value);
    const existing = diastolicByMinute.get(key);
    if (!existing || p.timestamp > existing.timestamp) {
      diastolicByMinute.set(key, { value, timestamp: p.timestamp });
    }
  }

  const commonKeys = Array.from(systolicByMinute.keys()).filter((k) =>
    diastolicByMinute.has(k)
  );
  commonKeys.sort((a, b) => a.localeCompare(b));

  const result: BloodPressureDataPoint[] = [];
  for (const minuteKey of commonKeys) {
    const sys = systolicByMinute.get(minuteKey)!;
    const dia = diastolicByMinute.get(minuteKey)!;
    const { date, fullDate } = formatTimestampToChartDate(sys.timestamp);
    result.push({
      date,
      fullDate,
      systolic: sys.value,
      diastolic: dia.value,
    });
  }
  return result;
}

/** Sleep API value to chart sleepHours: "7"→5, "7-9"→8, "9+"→10. Display: "<7", "7-9", "9+". */
function parseSleepValue(value: string): {
  sleepHours: number;
  valueDisplayLabel: string;
} {
  const raw = value.replace(/(^"|"$)/g, "").trim();
  if (raw === "9+") return { sleepHours: 10, valueDisplayLabel: "9+" };
  if (raw === "7-9") return { sleepHours: 8, valueDisplayLabel: "7-9" };
  if (
    raw === "7" ||
    raw === "6" ||
    raw === "5" ||
    raw === "4" ||
    raw === "3" ||
    raw === "2" ||
    raw === "1" ||
    raw === "0"
  )
    return { sleepHours: 5, valueDisplayLabel: "<7" };
  return { sleepHours: 5, valueDisplayLabel: "<7" };
}

/**
 * Map API health trend points to Sleep chart data.
 * API values: "7" (or "\"7\"") → chart 5, display "<7"; "7-9" → 8, "7-9"; "9+" → 10, "9+".
 */
export function mapHealthTrendToSleepChartData(
  points: HealthTrendDataPoint[]
): SleepChartDataPoint[] {
  return points
    .map((p) => {
      const { date, fullDate } = formatTimestampToChartDate(p.timestamp);
      const { sleepHours, valueDisplayLabel } = parseSleepValue(p.value);
      return {
        date,
        fullDate,
        sleepHours,
        valueDisplayLabel,
      };
    })
    .reverse();
}

export function mapHealthTrendsToMentalWellbeingChartData(
  points: HealthTrendDataPoint[]
): MentalWellbeingChartDataPoint[] {
  return points
    .map((p, i) => {
      const { date, fullDate } = formatTimestampToChartDate(p.timestamp);
      const score = Number.parseInt(p.value);
      return {
        date,
        fullDate,
        score,
        isSelected: i === 0,
      };
    })
    .reverse();
}

/** Exercise API value.value to chart minutesPerWeek: "0-150"→75, "150-300"→225, "300+"→375. */
function parseExerciseValue(value: string): {
  minutesPerWeek: number;
  valueDisplayLabel: string;
} {
  let range = value;
  try {
    const parsed = JSON.parse(value) as { value?: string };
    range = parsed?.value ?? value;
  } catch {
    // keep raw value
  }
  if (range === "300+")
    return { minutesPerWeek: 375, valueDisplayLabel: "300+" };
  if (range === "150-300")
    return { minutesPerWeek: 225, valueDisplayLabel: "150-300" };
  if (range === "0-150")
    return { minutesPerWeek: 75, valueDisplayLabel: "0-150" };
  return { minutesPerWeek: 75, valueDisplayLabel: "0-150" };
}

/**
 * Map API health trend points to Exercise chart data.
 * API value is JSON with .value: "0-150", "150-300", "300+" → chart 75, 225, 375.
 */
export function mapHealthTrendToExerciseChartData(
  points: HealthTrendDataPoint[]
): ExerciseChartDataPoint[] {
  return points
    .map((p) => {
      const { date, fullDate } = formatTimestampToChartDate(p.timestamp);
      const { minutesPerWeek, valueDisplayLabel } = parseExerciseValue(p.value);
      return {
        date,
        fullDate,
        minutesPerWeek,
        valueDisplayLabel,
      };
    })
    .reverse();
}
